import { DialogBackdropProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ITenant,
  TenantRequest,
  TenantRequestSchema,
} from '@monetize/types/app';
import { TIMEZONE_CONFIG } from '@monetize/utils/core';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../api/axios';
import { useCreateTenant, useUpdateTenant } from '../../api/tenantService';
import { useGetTimezones } from '../../api/timezoneService';
import { USER_TIMEZONE } from '../../constants/dates';
import { useAuth } from '../../services/auth0';
import { useFlags } from '../../services/launchDarkly';
import {
  MBox,
  MButton,
  MCenterModal,
  MCustomRadioGroup,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MLink,
  MStack,
  MTextarea,
} from '../Monetize';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

interface TenantFormBaseProps {
  existingTenant?: ITenant | null;
  isOpen: boolean;
  overlayProps?: DialogBackdropProps;
  onSaved?: (data: ITenant) => void;
}

interface TenantFormWithoutCloseProps extends TenantFormBaseProps {
  showCloseButton?: true;
  onClose: () => void;
}

interface TenantFormWithCloseProps extends TenantFormBaseProps {
  showCloseButton: false;
  onClose?: never;
}

export const TenantForm = ({
  existingTenant,
  isOpen,
  showCloseButton = true,
  onSaved,
  onClose,
}: TenantFormWithoutCloseProps | TenantFormWithCloseProps) => {
  const initialRef = React.useRef(null);
  const { createProductionTenant } = useFlags();
  const TENANT_OPTIONS = [
    {
      title: 'Test',
      value: true,
      description:
        'This is a test tenant, play around, make mistakes, learn how MonetizeNow works.',
    },
    {
      title: 'Live',
      disabled: !createProductionTenant,
      value: false,
      description: (
        <>
          If you are ready for a live tenant, please contact{' '}
          <MLink href="mailto:support@monetizenow.io" color="tIndigo.base">
            support@monetizenow.io
          </MLink>
        </>
      ),
    },
  ];

  const { currentTenant, setCurrentTenant } = useAuth();
  const { data: TimeZoneResponse } = useGetTimezones();
  const { mutateAsync: doCreateTenant, isPending: createLoading } =
    useCreateTenant();
  const { mutateAsync: doUpdateTenant, isPending: updateLoading } =
    useUpdateTenant();
  const timeZones = TimeZoneResponse?.timezones || [];
  const timeZonesByValue = TimeZoneResponse?.timezonesByValue || {};
  const isLoading = createLoading || updateLoading;

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<TenantRequest>({
    resolver: zodResolver(TenantRequestSchema),
    mode: 'onChange',
    defaultValues: {
      description: existingTenant?.description || '',
      testTenant: existingTenant?.testTenant ?? true,
      name: existingTenant?.name || '',
      timezone: existingTenant?.timezone ?? 'UTC',
    },
  });
  const watchTimezone = watch('timezone');

  const utcTimeZoneLabel = timeZonesByValue.UTC?.value;
  const userTimeZoneLabel =
    timeZonesByValue[TIMEZONE_CONFIG.userTimezone]?.label;

  const onSubmit = async (data: TenantRequest) => {
    try {
      let tenant;
      if (!existingTenant) {
        tenant = await doCreateTenant(data);
      } else {
        tenant = await doUpdateTenant({ id: existingTenant.id!, data });
        if (existingTenant.id === currentTenant?.id) {
          setCurrentTenant({ ...tenant });
        }
      }
      onSaved?.(tenant);
      onClose?.();
    } catch (err: any) {
      handleApiErrorToast(err);
    }
  };

  return (
    <MCenterModal
      initialFocusRef={initialRef}
      size="md"
      isOpen={isOpen}
      showCloseButton={showCloseButton}
      onClose={onClose || (() => {})}
      modalTitle={existingTenant?.id ? 'Edit Tenant' : 'New Tenant'}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack
          gap={8}
          direction="row"
          align="center"
          justify="center"
          flex={1}
        >
          <MButton
            variant="primary"
            type="submit"
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
            isDisabled={!isValid || !isDirty || isLoading}
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <MBox>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="tenant-form">
          <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
            <MGridItem colSpan={12}>
              <MFormField error={errors.name} label="Tenant Name" isRequired>
                <Controller
                  name="name"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <MInput
                      {...field}
                      placeholder="Ex: Tenant 1"
                      ref={initialRef}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField label="Description">
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ field: { value, ...rest } }) => (
                    <MTextarea value={value || ''} {...rest} />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField
                label="Time Zone"
                tooltip="This controls the time zone used for displaying date-time fields in MonetizeNow, emails, and PDF documents."
              >
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      useMainInputAsSearchInput
                      MainInputComponent={MainSearchInputV2}
                      items={timeZones || []}
                      itemTitle="displayLabel"
                      itemValue="value"
                      isLazy
                      {...field}
                    />
                  )}
                />
              </MFormField>
              <MButton
                variant="link"
                pl="1"
                size="sm"
                onClick={() =>
                  setValue(
                    'timezone',
                    watchTimezone === USER_TIMEZONE ? 'UTC' : USER_TIMEZONE,
                    { shouldDirty: true, shouldValidate: true },
                  )
                }
              >
                {watchTimezone === USER_TIMEZONE
                  ? `Set to default time zone (${utcTimeZoneLabel})`
                  : `Set to your time zone (${userTimeZoneLabel})`}
              </MButton>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField error={errors.testTenant} label="Select Tenant Type">
                <Controller
                  name="testTenant"
                  control={control}
                  defaultValue
                  disabled={!!existingTenant?.id}
                  render={({ field: { value, ...rest } }) => (
                    <MCustomRadioGroup
                      value={value as any}
                      my={1}
                      {...rest}
                      itemDescription="description"
                      items={TENANT_OPTIONS}
                      disabled={!!existingTenant?.id}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
          </MGrid>
        </form>
      </MBox>
    </MCenterModal>
  );
};
